import { OktaAuth } from '@okta/okta-auth-js';
import { PUBLIC_PATHS, isPublicPath } from './authConstants';
import { readOktaEnv, readBaseUrl, readOrigin } from './authEnv';

export { PUBLIC_PATHS, isPublicPath };

/**
 * Read Okta config from Vite env. All three string fields are required for
 * a working auth setup; `isOktaConfigured()` below checks that and is used
 * at the app root to render a "not configured" screen instead of throwing.
 *
 * The actual env reads are delegated to `./authEnv.js` so Jest can mock
 * them without having to parse `import.meta`.
 */
export const oktaConfig = {
  issuer: readOktaEnv('VITE_OKTA_ISSUER'),
  clientId: readOktaEnv('VITE_OKTA_CLIENT_ID'),
  redirectUri: readOktaEnv('VITE_OKTA_REDIRECT_URI'),
  scopes: ['openid', 'email', 'profile'],
};

/** True when all required Okta env vars are present. */
export function isOktaConfigured() {
  return Boolean(
    oktaConfig.issuer && oktaConfig.clientId && oktaConfig.redirectUri
  );
}

let _oktaAuth = null;

/**
 * Singleton factory for the OktaAuth instance. Throws with a clear,
 * actionable message listing the missing env vars if called before the
 * environment is configured — callers should gate with `isOktaConfigured()`
 * at the app root.
 */
export function getOktaAuth() {
  if (_oktaAuth) return _oktaAuth;

  const missing = [];
  if (!oktaConfig.issuer) missing.push('VITE_OKTA_ISSUER');
  if (!oktaConfig.clientId) missing.push('VITE_OKTA_CLIENT_ID');
  if (!oktaConfig.redirectUri) missing.push('VITE_OKTA_REDIRECT_URI');
  if (missing.length) {
    throw new Error(
      `Okta is not configured. Missing env var(s): ${missing.join(', ')}. ` +
        'See .env.example.'
    );
  }

  _oktaAuth = new OktaAuth({
    issuer: oktaConfig.issuer,
    clientId: oktaConfig.clientId,
    redirectUri: oktaConfig.redirectUri,
    scopes: oktaConfig.scopes,
    pkce: true,
  });
  return _oktaAuth;
}

/** Test-only: reset the cached OktaAuth singleton. */
export function __resetOktaAuthForTests() {
  _oktaAuth = null;
}

/**
 * Build the absolute post-logout redirect URL. Okta rejects relative paths
 * (and a bare `/`) in `signOut`'s `postLogoutRedirectUri`, so this joins
 * `window.location.origin` with Vite's `BASE_URL`.
 */
export function getPostLogoutRedirectUri() {
  const base = readBaseUrl() || '/';
  const origin = readOrigin();
  const normalizedBase = base.startsWith('/') ? base : `/${base}`;
  return `${origin}${normalizedBase}`.replace(/\/+$/, '/');
}
